import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PropertyRow {
  suburb: string | null;
  state: string | null;
  snapshot_count: number | null;
}

interface SnapshotRow {
  property_id: string;
  snapshot_date: string | null;
  properties: {
    suburb: string | null;
    state: string | null;
  } | null;
}

interface TrendAggregate {
  countsByProperty: Map<string, number>;
}

export interface SuburbSnapshotMetric {
  suburb: string;
  state: string;
  properties: number;
  avgSnapshotsPerProperty: number;
  deltaVsPrevious30d: number | null;
}

export interface SnapshotMetricsSummary {
  avgSnapshotsPerPropertyPerSuburb: number;
  deltaVsPrevious30d: number | null;
  suburbsMeasured: number;
}

interface SnapshotMetricsState {
  summary: SnapshotMetricsSummary;
  suburbs: SuburbSnapshotMetric[];
  isLoading: boolean;
}

const DEFAULT_STATE: SnapshotMetricsState = {
  summary: {
    avgSnapshotsPerPropertyPerSuburb: 0,
    deltaVsPrevious30d: null,
    suburbsMeasured: 0,
  },
  suburbs: [],
  isLoading: true,
};

const PERIOD_DAYS = 30;

function getSuburbKey(state: string, suburb: string): string {
  return `${state}::${suburb}`;
}

function normalizeLocation(suburb: string | null, state: string | null): { suburb: string; state: string } | null {
  if (!suburb || !state) return null;
  return { suburb: suburb.trim().toUpperCase(), state: state.trim().toUpperCase() };
}

export function useSnapshotMetrics() {
  const [state, setState] = useState<SnapshotMetricsState>(DEFAULT_STATE);

  const loadMetrics = useCallback(async () => {
    try {
      const { data: propertyRows, error: propertyError } = await (supabase as any)
        .from("properties")
        .select("suburb,state,snapshot_count");

      if (propertyError) throw propertyError;

      const suburbTotals = new Map<string, { state: string; suburb: string; properties: number; snapshots: number }>();

      for (const row of (propertyRows || []) as PropertyRow[]) {
        const location = normalizeLocation(row.suburb, row.state);
        if (!location) continue;
        const key = getSuburbKey(location.state, location.suburb);
        const existing = suburbTotals.get(key) || {
          state: location.state,
          suburb: location.suburb,
          properties: 0,
          snapshots: 0,
        };
        existing.properties += 1;
        existing.snapshots += row.snapshot_count || 0;
        suburbTotals.set(key, existing);
      }

      const now = new Date();
      const currentWindowStart = new Date(now.getTime() - PERIOD_DAYS * 24 * 60 * 60 * 1000);
      const previousWindowStart = new Date(now.getTime() - PERIOD_DAYS * 2 * 24 * 60 * 60 * 1000);

      const { data: snapshotRows, error: snapshotError } = await (supabase as any)
        .from("property_snapshots")
        .select("property_id,snapshot_date,properties!inner(suburb,state)")
        .gte("snapshot_date", previousWindowStart.toISOString());

      if (snapshotError) throw snapshotError;

      const currentPeriod = new Map<string, TrendAggregate>();
      const previousPeriod = new Map<string, TrendAggregate>();

      for (const row of (snapshotRows || []) as SnapshotRow[]) {
        if (!row.snapshot_date || !row.properties) continue;
        const snapshotTime = new Date(row.snapshot_date).getTime();
        if (Number.isNaN(snapshotTime)) continue;
        const location = normalizeLocation(row.properties.suburb, row.properties.state);
        if (!location) continue;

        const suburbKey = getSuburbKey(location.state, location.suburb);
        const target =
          snapshotTime >= currentWindowStart.getTime()
            ? currentPeriod
            : snapshotTime >= previousWindowStart.getTime()
              ? previousPeriod
              : null;

        if (!target) continue;
        const aggregate = target.get(suburbKey) || { countsByProperty: new Map<string, number>() };
        aggregate.countsByProperty.set(row.property_id, (aggregate.countsByProperty.get(row.property_id) || 0) + 1);
        target.set(suburbKey, aggregate);
      }

      const suburbMetrics: SuburbSnapshotMetric[] = [];
      for (const suburbEntry of suburbTotals.values()) {
        if (suburbEntry.properties === 0) continue;
        const avgSnapshotsPerProperty = suburbEntry.snapshots / suburbEntry.properties;
        const suburbKey = getSuburbKey(suburbEntry.state, suburbEntry.suburb);

        const current = currentPeriod.get(suburbKey);
        const previous = previousPeriod.get(suburbKey);

        const currentAvg = current
          ? Array.from(current.countsByProperty.values()).reduce((sum, count) => sum + count, 0) / current.countsByProperty.size
          : 0;
        const previousAvg = previous
          ? Array.from(previous.countsByProperty.values()).reduce((sum, count) => sum + count, 0) / previous.countsByProperty.size
          : 0;

        const deltaVsPrevious30d =
          current && previous ? currentAvg - previousAvg : null;

        suburbMetrics.push({
          suburb: suburbEntry.suburb,
          state: suburbEntry.state,
          properties: suburbEntry.properties,
          avgSnapshotsPerProperty,
          deltaVsPrevious30d,
        });
      }

      suburbMetrics.sort((a, b) => b.avgSnapshotsPerProperty - a.avgSnapshotsPerProperty);

      const suburbAverages = suburbMetrics.map((metric) => metric.avgSnapshotsPerProperty);
      const summaryAverage =
        suburbAverages.length > 0
          ? suburbAverages.reduce((sum, avg) => sum + avg, 0) / suburbAverages.length
          : 0;

      const deltas = suburbMetrics
        .map((metric) => metric.deltaVsPrevious30d)
        .filter((delta): delta is number => delta !== null);
      const summaryDelta = deltas.length > 0 ? deltas.reduce((sum, delta) => sum + delta, 0) / deltas.length : null;

      setState({
        summary: {
          avgSnapshotsPerPropertyPerSuburb: summaryAverage,
          deltaVsPrevious30d: summaryDelta,
          suburbsMeasured: suburbMetrics.length,
        },
        suburbs: suburbMetrics,
        isLoading: false,
      });
    } catch (error) {
      console.error("Error loading snapshot metrics:", error);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  useEffect(() => {
    loadMetrics();

    const channel = supabase
      .channel("snapshot_metrics")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "property_snapshots" },
        () => {
          loadMetrics();
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "properties" },
        () => {
          loadMetrics();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadMetrics]);

  return {
    ...state,
    refetch: loadMetrics,
  };
}
